import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { useBespokeSectionContext } from './BespokeSectionContext';
import { useQuestionFieldDisabled } from './useQuestionFieldDisabled';
import { QidTag } from '../../../dev/QidTag';
import {
  joinClasses,
  BESPOKE_FIELD_LABEL_CLASS,
  BESPOKE_FIELD_STACK_GAP_CLASS,
  BESPOKE_INPUT_COMPACT_CLASS,
  BESPOKE_EMPTY_STATE_CLASS,
  BESPOKE_EMPTY_STATE_CODE_CLASS,
} from './fieldClassSlots';
import { resolveControlClassName } from './resolveFieldClassNames';

export interface OASISSelectProps {
  qid: string;
  /** Display-only replacement for the schema prompt; answer binding still uses qid. */
  labelOverride?: string;
  hideLabel?: boolean;
  className?: string;
  style?: React.CSSProperties;
  innerClassName?: string;
  labelClassName?: string;
  controlClassName?: string;
  /** @deprecated Prefer `controlClassName`. */
  triggerClassName?: string;
  placeholder?: string;
}

export function OASISSelect({
  qid,
  labelOverride,
  hideLabel,
  className,
  style,
  innerClassName,
  labelClassName,
  controlClassName,
  triggerClassName,
  placeholder = 'Select an option',
}: OASISSelectProps) {
  const { answers, onAnswerChange, schemaMap } = useBespokeSectionContext();
  const question = schemaMap.get(qid);
  const inheritedDisabled = useQuestionFieldDisabled(question);

  const value = (answers[qid] as string) ?? '';
  const options = (question?.values ?? []).map(val => ({
    value: val,
    label: question?.value_description?.[val]
      ? `${val} - ${question.value_description[val]}`
      : val,
  }));
  const label =
    labelOverride ?? question?.question ?? question?.description ?? qid;
  const resolvedControlClassName = resolveControlClassName(
    controlClassName,
    triggerClassName
  );

  return (
    <div
      data-qid={qid}
      className={joinClasses('min-w-0', className)}
      style={style}
    >
      <QidTag qid={qid} />
      <div className={innerClassName ?? BESPOKE_FIELD_STACK_GAP_CLASS}>
        {!hideLabel && (
          <label
            className={joinClasses(BESPOKE_FIELD_LABEL_CLASS, labelClassName)}
          >
            {label}
          </label>
        )}
        {options.length === 0 ? (
          <p className={BESPOKE_EMPTY_STATE_CLASS}>
            No choices in loaded schema for{' '}
            <code className={BESPOKE_EMPTY_STATE_CODE_CLASS}>{qid}</code>.
            Select a RogerDoc schema + section that define choices for this
            dropdown.
          </p>
        ) : (
          <Select
            value={value}
            onValueChange={v => onAnswerChange(qid, v)}
            disabled={inheritedDisabled}
          >
            <SelectTrigger
              selectSize='default'
              className={joinClasses(
                BESPOKE_INPUT_COMPACT_CLASS,
                resolvedControlClassName
              )}
            >
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              {options.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
}
